import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ExternalPartyAssignmentDto} from 'features/core/cc/model/dto/entity/external-party-assignment.dto';
import {
    XxPersonExternalFlatQlService,
    XxPersonFlatRow
} from '../../../service/xx-person-external-flat-ql.service';
import {XxPersonExternalFlatTypeFieldDto} from '../../../model/dto/entity/xx-person-external-flat-type-field.dto';

/**
 * Dynamic column descriptor for one external side-car field.
 *
 * `key` is the `ext_<typeFieldId>` property on a {@link XxPersonFlatRow}; the
 * unique `*ExternalTypeField` id is the flattening key so two parties' same-named
 * fields never alias. `header` is the German display label from the type field.
 *
 * @ticket TM-1000001960
 */
interface XxExternalFlatColumn {
    /** Row property key, e.g. `ext_3` */
    key: string;
    /** Column header (German label of the type field) */
    header: string;
}

/**
 * Read-side demonstration component for the Clean-Core External Side-Car
 * bounded-1:N QL flatten (TM-1000001960).
 *
 * The component renders {@link XxPersonDto} rows flattened with their external
 * side-car fields for a selected {@link ExternalPartyAssignmentDto}. Per
 * `projects/external/04-forms-uf-widgets.md` §3 the display reuses the existing
 * flatten mechanism: each external field column is keyed by the unique
 * `*ExternalTypeField` id (`ext_<typeFieldId>`) and the external section is
 * labelled by the selected party name.
 *
 * Architecture note — why a self-contained component (NOT a WidgetFactory
 * `ql` data provider):
 * - The data comes from {@link XxPersonExternalFlatQlService}, which issues the
 *   bounded-external flatten via `QlQueryBuilder.boundedExternalVersionJoin` (a join
 *   parameter the generic widget data-provider cannot express) and returns
 *   pre-shaped {@link XxPersonFlatRow} rows.
 * - The external columns are RUNTIME-DYNAMIC (`ext_<typeFieldId>` discovered
 *   per assignment); the generic entity/QL widget table renders columns from
 *   fixed backend form metadata and cannot bind to columns whose ids are only
 *   known after the type-field lookup.
 * - The spec (§3 / §4) is explicit: no new widget type — reuse the existing
 *   flatten + fieldGroup mechanisms; the only frontend work is labelling the
 *   group by the party name and binding columns to `ext_<typeFieldId>`.
 * Hence the rendering is a read-only display table with a selector — exactly the
 * pattern the prompt's HARD RULES permit (a read-only display table + a
 * selector is fine here).
 *
 * @ticket TM-1000001960
 */
@Component({
    selector: 'mvs-xx-person-external-flat',
    templateUrl: './xx-person-external-flat.component.html',
    styleUrls: ['./xx-person-external-flat.component.scss'],
    standalone: false
})
export class XxPersonExternalFlatComponent implements OnInit, OnChanges, OnDestroy {

    /** True once the assignment selector list has finished its first load. */
    initialized = false;

    /** Available assignments for the selector. */
    assignments: ExternalPartyAssignmentDto[] = [];

    /** Currently selected assignment (drives the flatten query). */
    selectedAssignment: ExternalPartyAssignmentDto | null = null;

    /** Dynamic `ext_<typeFieldId>` columns for the selected assignment. */
    externalColumns: XxExternalFlatColumn[] = [];

    /** Flattened person rows (core fields + `ext_<id>` values). */
    rows: XxPersonFlatRow[] = [];

    /** True while the flatten query / type-field lookup is running. */
    loading = false;

    /** Holds a user-facing German error message, or null when there is none. */
    errorMessage: string | null = null;

    constructor(private flatService: XxPersonExternalFlatQlService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     *
     * @ticket TM-1000001960
     */
    initComponent(): void {
        this.refreshComponent();
    }

    /**
     * Refresh Component: (re)load the assignment selector list.
     *
     * @ticket TM-1000001960
     */
    refreshComponent(): void {
        this.loadAssignments();
    }

    /**
     * Process changes within Binding.
     *
     * @param changes the bound input changes
     * @ticket TM-1000001960
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }

    /**
     * Destroy component.
     *
     * @ticket TM-1000001960
     */
    ngOnDestroy(): void {
    }

    /**
     * Load the ExternalPartyAssignments that populate the selector.
     *
     * @ticket TM-1000001960
     */
    loadAssignments(): void {
        this.errorMessage = null;
        this.flatService.loadExternalPartyAssignments().subscribe({
            next: (assignments) => {
                this.assignments = assignments ?? [];
                this.initialized = true;
            },
            error: () => {
                this.errorMessage = 'Die externen Parteien / Zuordnungen konnten nicht geladen werden.';
                this.initialized = true;
            }
        });
    }

    /**
     * Selection handler: discover the dynamic `ext_<id>` columns for the chosen
     * assignment's version stream, then load the flattened person rows.
     *
     * VERSION-AXIS SPLIT (TM-1000001977): BOTH the column discovery and the value read
     * scope by the shared ExternalPartyVersion stream carried on the assignment as
     * {@link ExternalPartyAssignmentDto.externalPartyVersionDtoId} — the legacy
     * assignment-scoped FK was removed from `xx.XxPersonExternalFlatType`. Two assignments
     * that point at the same version id therefore discover the SAME columns AND render the
     * SAME side-car data. Type fields and rows are loaded together so the table never
     * renders columns without data.
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    onAssignmentSelect(): void {
        this.externalColumns = [];
        this.rows = [];
        this.errorMessage = null;

        if (!this.selectedAssignment) {
            return;
        }

        // TM-1000001977: discovery AND read both scope by the shared version stream.
        const versionId = this.selectedAssignment.externalPartyVersionDtoId;

        if (versionId == null) {
            this.errorMessage = 'Für diese Zuordnung ist kein Versionsstrom (ExternalPartyVersion) hinterlegt.';
            return;
        }

        this.loading = true;

        this.flatService.loadTypeFields(versionId).subscribe({
            next: (typeFields) => {
                const fields = typeFields ?? [];
                this.externalColumns = this.buildColumns(fields);
                const typeFieldIds = fields.map(f => f.id);
                this.loadRows(versionId, typeFieldIds);
            },
            error: () => {
                this.loading = false;
                this.errorMessage = 'Die externen Felddefinitionen konnten nicht geladen werden.';
            }
        });
    }

    /**
     * Build the dynamic column descriptors from the type-field DTOs. The column
     * key is `ext_<typeFieldId>` (the unique flattening key); the header is the
     * field label, falling back to the field id when no label is configured.
     *
     * @ticket TM-1000001960
     */
    private buildColumns(fields: XxPersonExternalFlatTypeFieldDto[]): XxExternalFlatColumn[] {
        return [...fields]
            .sort((a, b) => (a.orderPriority ?? 0) - (b.orderPriority ?? 0))
            .map(field => ({
                key: `ext_${field.id}`,
                header: field.label ?? `Feld ${field.id}`
            }));
    }

    /**
     * Run the version-scoped bounded-external flatten and store the resulting rows.
     *
     * @param versionId    the ExternalPartyVersion stream id bound into the join ON
     * @param typeFieldIds the `*TypeField` ids to flatten into `ext_<id>` columns
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    private loadRows(versionId: number, typeFieldIds: number[]): void {
        this.flatService.loadPersonsWithExternal(versionId, typeFieldIds).subscribe({
            next: (rows) => {
                this.rows = rows ?? [];
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.errorMessage = 'Die Personendaten mit externen Feldern konnten nicht geladen werden.';
            }
        });
    }

    /**
     * Section title labelled by the selected party (per spec §3 Layout A).
     * Example: Externe Daten — NÜRNBERGER Privat (GDV V1997).
     *
     * @ticket TM-1000001960
     */
    get externalSectionTitle(): string {
        if (!this.selectedAssignment) {
            return 'Externe Daten';
        }
        const partyName = this.resolveAssignmentLabel(this.selectedAssignment);
        return `Externe Daten — ${partyName}`;
    }

    /**
     * Human-readable label for an assignment. Uses the assignment name (from DtoDetail)
     * and the resolved ObjectType name. Appends the shared ExternalPartyVersion stream
     * name so two assignments that share one version render the same "(Version: …)" suffix.
     *
     * TM-1000001977 Phase 3.6: externalPartyDtoName / protocol / version deleted from
     * ExternalPartyAssignment; targetObjectTypeDtoName renamed to objectTypeDtoName.
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    resolveAssignmentLabel(assignment: ExternalPartyAssignmentDto): string {
        const parts: string[] = [];

        let host = assignment.name ?? `Zuordnung ${assignment.id}`;
        if (assignment.objectTypeDtoName) {
            host = `${host} — ${assignment.objectTypeDtoName}`;
        }
        parts.push(host);

        // TM-1000001977: shared version-stream name (new axis).
        if (assignment.externalPartyVersionDtoName) {
            parts.push(`(Version: ${assignment.externalPartyVersionDtoName})`);
        }

        return parts.join(' ');
    }

    /**
     * Render a flattened external value for the table cell. NULL / undefined
     * side-car values render as an em dash so absent fields are visible.
     *
     * @ticket TM-1000001960
     */
    displayValue(row: XxPersonFlatRow, key: string): string {
        const value = row[key];
        if (value === null || value === undefined || value === '') {
            return '—';
        }
        return String(value);
    }
}
